import type { BackendModuleKey } from "./backendModuleKeys";
import type { ModuleAccessKey } from "./accessRequests";
import type { User } from "../stores/authStore";
import {
  MODULE_GATES,
  SETTINGS_TAB_GATE_IDS,
  gatePermissionCodes,
  resolveRouteGate,
  type ModuleGateId,
  type ResolvedRouteGate,
  type RouteGatePlaceholder,
} from "./moduleGates";

export type { ModuleGateId, RouteGatePlaceholder, ResolvedRouteGate };
export {
  MODULE_GATES,
  ROUTE_GATE_BINDINGS,
  SETTINGS_TAB_GATE_IDS,
  applyBackendModulePermissionMap,
  gatePermissionCodes,
  resolveRouteGate,
} from "./moduleGates";

/** Permission required to open a route (any one of the listed codes grants access). */
export type RoutePermissionRule = {
  match: (pathname: string) => boolean;
  permissions: string[];
  adminOnly?: boolean;
};

/** Normalize pathname for route permission lookup (trailing slash except root). */
export function normalizeRoutePath(pathname: string): string {
  if (!pathname || pathname === "/") return "/";
  const withoutTrailing = pathname.replace(/\/+$/, "");
  return withoutTrailing === "" ? "/" : withoutTrailing;
}

/**
 * Nav / route permission map (aligned with familyhub-backend REST scopes).
 * Grant checks for gated routes use MODULE_GATES via resolveRouteGate / userHasGateGrant.
 */
export const ROUTE_PERMISSION_RULES: RoutePermissionRule[] = [
  { match: (p) => p === "/", permissions: [] },
  { match: (p) => p === "/documents/queue", permissions: [], adminOnly: true },
  { match: (p) => p === "/ai-chat", permissions: [...MODULE_GATES.ai_chat.permissions] },
  { match: (p) => p.startsWith("/documents"), permissions: [...MODULE_GATES.documents.permissions] },
  { match: (p) => p.startsWith("/properties"), permissions: [...MODULE_GATES.apartments.permissions] },
  { match: (p) => p === "/utility-migrations", permissions: [...MODULE_GATES.apartments.permissions] },
  { match: (p) => p.startsWith("/bundles"), permissions: [...MODULE_GATES.documents.permissions] },
  { match: (p) => p.startsWith("/family"), permissions: [...MODULE_GATES.family.permissions] },
  { match: (p) => p.startsWith("/payments"), permissions: [...MODULE_GATES.payments.permissions] },
  { match: (p) => p.startsWith("/budget"), permissions: [...MODULE_GATES.budget.permissions] },
  { match: (p) => p === "/estate-planner", permissions: [...MODULE_GATES.budget.permissions] },
  { match: (p) => p.startsWith("/tasks"), permissions: [...MODULE_GATES.tasks.permissions] },
  { match: (p) => p.startsWith("/finances"), permissions: [...MODULE_GATES.finances.permissions] },
  { match: (p) => p.startsWith("/career"), permissions: [...MODULE_GATES.career.permissions] },
  { match: (p) => p.startsWith("/knowledge"), permissions: [...MODULE_GATES.knowledge.permissions] },
  { match: (p) => p.startsWith("/settings"), permissions: [] },
];

/** @deprecated Use SETTINGS_TAB_GATE_IDS */
export const SETTINGS_TAB_PERMISSIONS: Record<string, string[]> = {
  integrations: [...MODULE_GATES.integrations.permissions],
  apiAccess: [...MODULE_GATES.integrations.permissions],
};

/** Derived from MODULE_GATES for access-request modules. */
export const MODULE_PERMISSION_CODES: Record<ModuleAccessKey, readonly string[]> = {
  apartments: gatePermissionCodes("apartments"),
  payments: gatePermissionCodes("payments"),
  budget: gatePermissionCodes("budget"),
  documents: gatePermissionCodes("documents"),
  tasks: gatePermissionCodes("tasks"),
  reminders: gatePermissionCodes("reminders"),
  knowledge: gatePermissionCodes("knowledge"),
  family: gatePermissionCodes("family"),
  ai_chat: gatePermissionCodes("ai_chat"),
  integrations: gatePermissionCodes("integrations"),
};

/** Admin-only nav entries — hidden from viewers (no access-request path). */
export const NAV_ADMIN_ONLY_HREFS = new Set<string>(["/documents/queue"]);

/** Dashboard widgets map to the same scopes as their full pages. */
export const DASHBOARD_FETCH_PERMISSIONS = {
  documents: [...MODULE_GATES.documents.permissions],
  payments: [...MODULE_GATES.payments.permissions],
  contracts: [...MODULE_GATES.apartments.permissions],
  budget: [...MODULE_GATES.budget.permissions],
} as const;

export function getRoutePermissionRule(pathname: string): RoutePermissionRule | undefined {
  const normalized = normalizeRoutePath(pathname);
  return ROUTE_PERMISSION_RULES.find((rule) => rule.match(normalized));
}

export function userHasFullAccess(user: User | null): boolean {
  if (!user) return false;
  return user.role === "admin" || user.role === "family";
}

export function collectGrantCodes(
  user: User | null,
  apiGrants?: readonly string[] | null,
): readonly string[] {
  if (!user) return apiGrants ?? [];
  return apiGrants?.length ? [...user.permissions, ...apiGrants] : user.permissions;
}

export function userHasAnyPermission(
  user: User | null,
  codes: readonly string[],
  apiGrants?: readonly string[] | null,
): boolean {
  if (!user) return false;
  if (userHasFullAccess(user)) return true;
  if (codes.length === 0) return true;
  const grants = collectGrantCodes(user, apiGrants);
  return grants.includes("*") || codes.some((code) => grants.includes(code));
}

export function userHasGateGrant(
  user: User | null,
  gateId: ModuleGateId,
  apiGrants?: readonly string[] | null,
): boolean {
  return userHasAnyPermission(user, gatePermissionCodes(gateId), apiGrants);
}

export function userCanAccessPath(
  user: User | null,
  pathname: string,
  apiGrants?: readonly string[] | null,
): boolean {
  if (!user) return false;
  const normalized = normalizeRoutePath(pathname);
  if (NAV_ADMIN_ONLY_HREFS.has(normalized) && user.role !== "admin") {
    return false;
  }

  const gate = resolveRouteGate(pathname);
  if (gate) {
    return userHasAnyPermission(user, gate.permissions, apiGrants);
  }

  const rule = getRoutePermissionRule(pathname);
  if (!rule) return true;
  if (rule.adminOnly && user.role !== "admin") return false;
  return userHasAnyPermission(user, rule.permissions, apiGrants);
}

export function canAccessSettingsTab(
  user: User | null,
  tabId: string,
  apiGrants?: readonly string[] | null,
): boolean {
  if (!user) return false;
  const gateId = SETTINGS_TAB_GATE_IDS[tabId];
  if (!gateId) return true;
  return userHasGateGrant(user, gateId, apiGrants);
}

export type RouteGateConfig = ResolvedRouteGate & {
  /** Module key for access-request API + pending lookup (wire or placeholder id). */
  module: ModuleAccessKey | BackendModuleKey;
};

export function getRouteGateConfig(pathname: string): RouteGateConfig | null {
  const normalized = normalizeRoutePath(pathname);
  if (NAV_ADMIN_ONLY_HREFS.has(normalized)) {
    return null;
  }
  const resolved = resolveRouteGate(pathname);
  if (!resolved) {
    return null;
  }
  const module =
    resolved.accessRequestModule ?? resolved.placeholderModule;
  return { ...resolved, module };
}

/** @deprecated Use getRouteGateConfig */
export function isDec005BlurRoute(pathname: string): boolean {
  return getRouteGateConfig(pathname) !== null;
}

/** DEC-006: hide only admin-only entries; permission-gated items stay visible (greyed). */
export function isNavItemVisible(
  user: User | null,
  href: string,
  options?: { adminOnly?: boolean },
): boolean {
  if (!user) return false;
  const normalized = normalizeRoutePath(href);
  if (options?.adminOnly || NAV_ADMIN_ONLY_HREFS.has(normalized)) {
    return user.role === "admin";
  }
  return true;
}

export function getNavModuleForHref(href: string): BackendModuleKey | null {
  return getRouteGateConfig(href)?.accessRequestModule ?? null;
}

export function getNavGateIdForHref(href: string): ModuleGateId | null {
  return getRouteGateConfig(href)?.gateId ?? null;
}
